import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Search, X, Settings, Eye, ArrowUpDown, ArrowUp, ArrowDown, Loader2, Phone, Mail, StickyNote, UserCog, Flag, CheckCircle2, XCircle, Clock, FileText, Sparkles, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDisputesPageDataGraphQL } from "@/hooks/useDisputesGraphQL";
import { useQuickUpdateDisputes, useDisputePriorities, type GetDisputesParams } from "@/hooks/useDisputesData";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { toast } from "sonner";
import type { DisputeUI } from "@/types/ui/disputes";
import { LogCallModal, SendEmailModal, AddNoteModal } from "@/components/collections/action-modals";
import { useUsersList } from "@/hooks/useUsersData";
import { ConsolidatedActivityTimeline } from "@/components/collections/consolidated-activity-timeline";
import { useDisputeRecentActivities } from "@/hooks/useDisputeRecentActivities";
import { getTotalLevelsForDisputeType } from "@/lib/utils/dispute-workflow-utils";
import { useAddDisputeNote } from "@/hooks/useDisputeNotes";
import { ResolveDisputeModal } from "@/components/disputes/resolve-dispute-modal";

const DEFAULT_DISPUTE_TAB_COLUMNS: ColumnDefinition[] = [
  { id: 'disputeId', label: 'Dispute ID', visible: true, order: 0 },
  { id: 'customer', label: 'Customer', visible: true, order: 1 },
  { id: 'type', label: 'Type', visible: true, order: 2 },
  { id: 'amount', label: 'Amount', visible: true, order: 3 },
  { id: 'status', label: 'Status', visible: true, order: 4 },
  { id: 'priority', label: 'Priority', visible: true, order: 5 },
  { id: 'workflowProgress', label: 'Workflow Progress', visible: true, order: 6 },
  { id: 'assignedTo', label: 'Assigned To', visible: true, order: 7 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 8 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 9 },
  { id: 'workflow', label: 'Workflow', visible: false, order: 10 },
  { id: 'description', label: 'Description', visible: false, order: 11 },
];

interface DisputesTabProps {
  customerId: string;
  customerName: string;
  isConsolidatedView?: boolean;
}

const statusColors = {
  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Under Review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Escalated": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "Resolved": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Closed": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
};

const priorityColors = {
  "Low": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  "Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "High": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Critical": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

// Mock documents for detail view
const documents = [
  { name: "Original Invoice.pdf", size: "245 KB", date: "2024-01-15" },
  { name: "Service Agreement.pdf", size: "1.2 MB", date: "2024-01-15" },
  { name: "Customer Email.msg", size: "45 KB", date: "2024-01-16" },
];

function UpdatePriorityModal({
  open,
  onOpenChange,
  selectedCount,
  selectedDisputeId,
  priorities,
  value,
  onValueChange,
  onConfirm,
}: Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  selectedDisputeId: string | undefined;
  priorities: Array<{ code: string; name: string; id?: number }>;
  value: string;
  onValueChange: (value: string) => void;
  onConfirm: () => void;
}>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-primary" />
            Update Priority
          </DialogTitle>
          <DialogDescription>
            {selectedCount === 1
              ? <>Change the priority for dispute <span className="font-semibold">{selectedDisputeId}</span></>
              : <>Update the priority for <span className="font-semibold">{selectedCount} disputes</span></>
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label>New Priority</Label>
          <Select value={value} onValueChange={onValueChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select priority..." />
            </SelectTrigger>
            <SelectContent>
              {priorities.map((p) => (
                <SelectItem key={p.code} value={p.name}>
                  <div className="flex items-center gap-2">
                    <Badge className={priorityColors[p.name as keyof typeof priorityColors]}>{p.name}</Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} disabled={!value} className="gap-2">
            <Flag className="h-4 w-4" />
            {selectedCount > 1 ? `Update ${selectedCount} Disputes` : "Update Priority"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DisputesTab({ customerId, customerName, isConsolidatedView = false }: DisputesTabProps) {
  const navigate = useNavigate();
  const customerIdNum = parseInt(customerId);

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedDispute, setSelectedDispute] = useState<DisputeUI | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Quick action modal states
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignUserId, setReassignUserId] = useState("");
  const [updatePriorityOpen, setUpdatePriorityOpen] = useState(false);
  const [updatePriorityValue, setUpdatePriorityValue] = useState("");
  const [closeDisputeOpen, setCloseDisputeOpen] = useState(false);
  const [closeReason, setCloseReason] = useState<'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other'>('duplicate');
  const [closeNote, setCloseNote] = useState('');
  const [resolveDisputeOpen, setResolveDisputeOpen] = useState(false);
  const [reopenDisputeOpen, setReopenDisputeOpen] = useState(false);

  // Build server-side query params
  const queryParams = useMemo<GetDisputesParams>(() => {
    const params: GetDisputesParams = {
      customerId: customerIdNum,
      page,
      pageSize,
      sortBy,
      sortOrder,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (statusFilter !== "all") {
      params.status = statusFilter;
    }

    if (priorityFilter !== "all") {
      params.priority = priorityFilter;
    }

    return params;
  }, [customerIdNum, page, pageSize, sortBy, sortOrder, debouncedSearch, statusFilter, priorityFilter]);

  // Fetch disputes with server-side params via GraphQL (mode-aware: mock filtering in demo, GraphQL in live)
  const { disputes: filteredDisputes, pagination, isLoading, refetch: refetchDisputes } = useDisputesPageDataGraphQL(queryParams);

  // Fetch recent activities for the selected dispute
  const { data: disputeActivityData } = useDisputeRecentActivities(selectedDispute?.id);

  // Quick update mutation (same as All Disputes page)
  const quickUpdateDisputes = useQuickUpdateDisputes();

  // Users list for reassign
  const { data: users = [] } = useUsersList();
  const addDisputeNoteMutation = useAddDisputeNote();

  // Dispute priorities
  const { data: disputePriorities = [] } = useDisputePriorities();

  // Derived: selected dispute IDs as array
  const selectedDisputeIds = Array.from(selectedRows);

  // Derived: single selected dispute object
  const singleSelectedDispute = selectedRows.size === 1
    ? filteredDisputes.find(d => d.id === selectedDisputeIds[0]) ?? null
    : null;

  // Derive the statuses of all selected disputes and determine quick action visibility
  const { selectedDisputeObjects, shouldHideAllActions, shouldShowReopenOnly } = useMemo(() => {
    const objs = selectedDisputeIds
      .map(id => filteredDisputes.find(d => d.id === id))
      .filter((d): d is DisputeUI => d !== undefined);
    const hasAnyResolved = objs.some(d => d.status === "Resolved");
    const allClosed = objs.length > 0 && objs.every(d => d.status === "Closed");
    const hasAnyClosed = objs.some(d => d.status === "Closed");
    const hasMixedWithClosed = hasAnyClosed && !allClosed;
    return {
      selectedDisputeObjects: objs,
      shouldHideAllActions: hasAnyResolved || hasMixedWithClosed,
      shouldShowReopenOnly: allClosed,
    };
  }, [selectedDisputeIds, filteredDisputes]);

  const renderQuickActions = () => {
    if (shouldShowReopenOnly) {
      return (
        <Button variant="secondary" size="sm" onClick={() => setReopenDisputeOpen(true)} className="gap-2">
          <RotateCcw className="h-4 w-4" />
          Reopen
        </Button>
      );
    }
    if (shouldHideAllActions) return null;
    if (selectedRows.size === 1) {
      return (
        <>
          <Button variant="secondary" size="sm" onClick={() => setLogCallOpen(true)} className="gap-2">
            <Phone className="h-4 w-4" />
            Log Call
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setSendEmailOpen(true)} className="gap-2">
            <Mail className="h-4 w-4" />
            Send Email
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAddNoteOpen(true)} className="gap-2">
            <StickyNote className="h-4 w-4" />
            Add Note
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setReassignOpen(true)} className="gap-2">
            <UserCog className="h-4 w-4" />
            Reassign User
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setUpdatePriorityOpen(true)} className="gap-2">
            <Flag className="h-4 w-4" />
            Update Priority
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setResolveDisputeOpen(true)} className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Resolve Dispute
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setCloseDisputeOpen(true)} className="gap-2">
            <XCircle className="h-4 w-4" />
            Close Dispute
          </Button>
        </>
      );
    }
    if (selectedRows.size > 1) {
      return (
        <>
          <Button variant="secondary" size="sm" onClick={() => setReassignOpen(true)} className="gap-2">
            <UserCog className="h-4 w-4" />
            Reassign User
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setUpdatePriorityOpen(true)} className="gap-2">
            <Flag className="h-4 w-4" />
            Update Priority
          </Button>
        </>
      );
    }
    return null;
  };

  const renderReopenDialogDescription = () => {
    const statusText = selectedDisputeObjects.some(d => d.currentLevel !== 1)
      ? "its appropriate status based on workflow level"
      : "New status";
    if (selectedDisputeIds.length === 1) {
      return <>Are you sure you want to reopen dispute <span className="font-semibold">{selectedDisputeIds[0]}</span>? Each dispute will be set to {statusText}.</>;
    }
    return <>Are you sure you want to reopen <span className="font-semibold">{selectedDisputeIds.length} disputes</span>? Each dispute will be set to {statusText}.</>;
  };

  // Search change handler (debounce handles page reset via useEffect)
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handlePriorityFilterChange = useCallback((value: string) => {
    setPriorityFilter(value);
    setPage(1);
  }, []);

  // Map column IDs to backend sort field names
  const sortableColumnMap: Record<string, string> = {
    disputeId: 'disputeId',
    customer: 'customer',
    type: 'type',
    amount: 'amount',
    status: 'status',
    priority: 'priority',
    assignedTo: 'assignedTo',
    dueDate: 'dueDate',
    createdDate: 'createdAt',
  };

  const handleSort = useCallback((columnId: string) => {
    const backendField = sortableColumnMap[columnId];
    if (!backendField) return;

    if (sortBy === backendField) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        // Reset to default sort
        setSortBy('createdAt');
        setSortOrder('desc');
      }
    } else {
      setSortBy(backendField);
      setSortOrder('asc');
    }
    setPage(1);
  }, [sortBy, sortOrder]);

  const getSortIcon = useCallback((columnId: string) => {
    const backendField = sortableColumnMap[columnId];
    if (!backendField) return null;

    if (sortBy !== backendField) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    if (sortOrder === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />;
    }
    return <ArrowDown className="ml-1 h-3 w-3" />;
  }, [sortBy, sortOrder]);

  // Column preferences
  const { columns, savePreferences, resetToDefaults } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CustomerDetailDisputes, DEFAULT_DISPUTE_TAB_COLUMNS);
  
  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);
  
  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredDisputes.map(d => d.id)));
    } else {
      setSelectedRows(new Set());
    }
  };
  
  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };
  
  const isAllSelected = filteredDisputes.length > 0 && selectedRows.size === filteredDisputes.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < filteredDisputes.length;
  
  // Action handlers
  const handleViewDetails = (dispute: DisputeUI) => {
    navigate(`/disputes/all/${dispute.id}`, { state: { from: 'customer-detail' } });
  };
  
  const handleClearSelection = () => {
    setSelectedRows(new Set());
  };

  // ── Quick action handlers ──────────────────────────────────────────────────

  const handleQuickAddNote = async (content: string) => {
    const disputeNumericId = singleSelectedDispute?.numericId;
    if (disputeNumericId) {
      try {
        await addDisputeNoteMutation.mutateAsync({
          disputeId: disputeNumericId.toString(),
          content,
          userId: "0",
          userName: "Current User",
        });
        toast.success("Note added successfully");
      } catch {
        toast.error("Failed to add note");
        throw new Error("Failed to add note");
      }
    } else {
      toast.success("Note added successfully");
    }
  };

  const handleQuickReassign = async () => {
    if (!reassignUserId) return;
    const user = users.find(u => u.id.toString() === reassignUserId);
    const userName = user ? `${user.firstName} ${user.lastName}` : reassignUserId;
    const count = selectedDisputeIds.length;
    await quickUpdateDisputes.mutateAsync(
      selectedDisputeIds.map(id => ({ disputeStringId: id, numericId: filteredDisputes.find(d => d.id === id)?.numericId, assignedTo: userName, assignedToUserId: parseInt(reassignUserId, 10) }))
    );
    toast.success(`${count} dispute${count > 1 ? "s" : ""} reassigned to ${userName}`);
    setReassignUserId("");
    setReassignOpen(false);
    refetchDisputes();
  };

  const handleQuickUpdatePriority = async () => {
    if (!updatePriorityValue) return;
    const count = selectedDisputeIds.length;
    const matchedPriority = disputePriorities.find(p => p.name === updatePriorityValue);
    await quickUpdateDisputes.mutateAsync(
      selectedDisputeIds.map(id => ({ disputeStringId: id, numericId: filteredDisputes.find(d => d.id === id)?.numericId, priority: updatePriorityValue, priorityId: matchedPriority?.id }))
    );
    toast.success(`Priority updated to ${updatePriorityValue} for ${count} dispute${count > 1 ? "s" : ""}`);
    setUpdatePriorityValue("");
    setUpdatePriorityOpen(false);
    refetchDisputes();
  };

  const handleQuickCloseDispute = async () => {
    if (closeReason === 'other' && !closeNote.trim()) return;
    const disputeId = singleSelectedDispute?.id;
    if (!disputeId) return;
    const closeReasonLabels = {
      duplicate: 'Duplicate Entry',
      customer_withdrew: 'Customer Withdrew Dispute',
      invalid_dispute: 'Invalid Dispute',
      other: 'Other',
    };
    const composedCloseNote = closeNote
      ? `Dispute closed - Reason: ${closeReasonLabels[closeReason]}\n\nNote: ${closeNote}`
      : `Dispute closed - Reason: ${closeReasonLabels[closeReason]}`;
    await quickUpdateDisputes.mutateAsync([{
      disputeStringId: disputeId,
      numericId: singleSelectedDispute?.numericId,
      status: "Closed",
      closedReason: closeReason,
      closedDate: new Date().toISOString().split("T")[0],
      closedNote: composedCloseNote,
    }]);
    if (singleSelectedDispute?.numericId) {
      await addDisputeNoteMutation.mutateAsync({
        disputeId: singleSelectedDispute.numericId.toString(),
        content: composedCloseNote,
        userId: "0",
        userName: "Current User",
      });
    }
    toast.success(`Dispute ${disputeId} closed`);
    setCloseReason('duplicate');
    setCloseNote('');
    setCloseDisputeOpen(false);
    setSelectedRows(new Set());
    refetchDisputes();
  };

  const handleQuickResolveDispute = useCallback(async (outcome: 'upheld' | 'denied' | 'partial', amount: string, notes: string) => {
    const disputeId = singleSelectedDispute?.id;
    if (!disputeId) return;
    await quickUpdateDisputes.mutateAsync([{
      disputeStringId: disputeId,
      numericId: singleSelectedDispute?.numericId,
      status: "Resolved",
      resolutionNotes: notes,
      resolutionOutcome: outcome,
      resolutionAmount: outcome === 'partial' ? parseFloat(amount) : singleSelectedDispute?.amount ?? null,
      resolutionDate: new Date().toISOString().split("T")[0],
      resolvedBy: "Current User",
    }]);
    toast.success(`Dispute ${disputeId} marked as resolved`);
    setResolveDisputeOpen(false);
    setSelectedRows(new Set());
    refetchDisputes();
  }, [singleSelectedDispute, quickUpdateDisputes, refetchDisputes]);

  const handleReopenDispute = async () => {
    const updates = selectedDisputeObjects.map(d => ({
      disputeStringId: d.id,
      numericId: d.numericId,
      status: d.currentLevel === 1 ? "New" : "In Progress",
    }));
    await quickUpdateDisputes.mutateAsync(updates);
    const count = updates.length;
    toast.success(`${count} dispute${count > 1 ? "s" : ""} reopened`);
    setReopenDisputeOpen(false);
    setSelectedRows(new Set());
    refetchDisputes();
  };

  // Helper function to render table cell content based on column ID
  const renderTableCell = (columnId: string, dispute: DisputeUI) => {
    switch (columnId) {
      case 'companyId':
        // In consolidated view, simulate different company IDs
        // In real implementation, this would come from the API
        return `COMP-${Math.floor(Math.random() * 3) + 1}`;
      case 'disputeId':
        return (
          <button
            onClick={() => handleViewDetails(dispute)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
          >
            {dispute.id}
          </button>
        );
      case 'customer':
        return dispute.customer;
      case 'type':
        return dispute.type;
      case 'amount':
        return `£${dispute.amount.toLocaleString()}`;
      case 'status':
        return (
          <Badge className={statusColors[dispute.status as keyof typeof statusColors]}>
            {dispute.status}
          </Badge>
        );
      case 'priority':
        return (
          <Badge className={priorityColors[dispute.priority as keyof typeof priorityColors]}>
            {dispute.priority}
          </Badge>
        );
      case 'workflowProgress': {
        const totalLevels = dispute.totalLevels || getTotalLevelsForDisputeType(dispute.type);
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
              <div
                className="bg-primary h-2 rounded-full"
                style={{ width: `${(dispute.currentLevel / totalLevels) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {dispute.currentLevel}/{totalLevels}
            </span>
          </div>
        );
      }
      case 'assignedTo':
        return dispute.assignedTo;
      case 'dueDate': {
        if (!dispute.dueDate) return "—";
        const dueDate = new Date(dispute.dueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDateNorm = new Date(dueDate);
        dueDateNorm.setHours(0, 0, 0, 0);
        const isOverdue = dueDateNorm < today;
        const isDueToday = dueDateNorm.getTime() === today.getTime();
        let dueDateColorClass = "text-muted-foreground";
        if (isOverdue) {
          dueDateColorClass = "text-red-600 dark:text-red-400 font-medium";
        } else if (isDueToday) {
          dueDateColorClass = "text-amber-600 dark:text-amber-400 font-medium";
        }
        return (
          <span className={cn(dueDateColorClass)}>
            {format(dueDate, "MMM dd, yyyy")}
          </span>
        );
      }
      case 'createdDate':
        return format(new Date(dispute.createdDate), "MMM dd, yyyy");
      case 'workflow':
        return dispute.workflow;
      case 'description':
        return (
          <span className="truncate max-w-[200px] block" title={dispute.description}>
            {dispute.description}
          </span>
        );
      default:
        return null;
    }
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={visibleColumns.length + (isConsolidatedView ? 2 : 1)} className="h-64 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading disputes...</p>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    if (filteredDisputes.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={visibleColumns.length + (isConsolidatedView ? 2 : 1)} className="text-center text-muted-foreground py-8">
            {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all'
              ? 'No disputes found matching your criteria'
              : 'No disputes recorded for this customer'}
          </TableCell>
        </TableRow>
      );
    }
    return filteredDisputes.map((dispute) => {
      const isSelected = selectedRows.has(dispute.id);
      return (
        <TableRow
          key={dispute.id}
          className={isSelected ? "bg-muted/50" : ""}
        >
          <TableCell>
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => handleSelectRow(dispute.id, checked as boolean)}
              aria-label={`Select dispute ${dispute.id}`}
            />
          </TableCell>
          {isConsolidatedView && (
            <TableCell>{renderTableCell('companyId', dispute)}</TableCell>
          )}
          {visibleColumns.map((column) => (
            <TableCell key={column.id}>
              {renderTableCell(column.id, dispute)}
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  const renderReopenDialog = () => (
    <Dialog open={reopenDisputeOpen} onOpenChange={setReopenDisputeOpen}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            Reopen Dispute{selectedDisputeIds.length > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            {renderReopenDialogDescription()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setReopenDisputeOpen(false)}>Cancel</Button>
          <Button onClick={handleReopenDispute} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Confirm Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex flex-col">
            <CardTitle className="text-base font-medium">
              Dispute Records
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({pagination?.totalItems ?? filteredDisputes.length})
              </span>
            </CardTitle>
            <CardDescription>
              Customer disputes for {customerName}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-1 ml-4 justify-end">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by dispute ID, type, description..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={handlePriorityFilterChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setColumnConfigOpen(true)}
                    aria-label="Configure Columns"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure Columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all disputes"
                    className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                  />
                </TableHead>
                {isConsolidatedView && (
                  <TableHead>Company ID</TableHead>
                )}
                {visibleColumns.map((column) => {
                  const isSortable = column.id in sortableColumnMap;
                  return (
                    <TableHead
                      key={column.id}
                      className={isSortable ? "cursor-pointer select-none" : ""}
                      onClick={isSortable ? () => handleSort(column.id) : undefined}
                    >
                      <span className="flex items-center">
                        {column.label}
                        {isSortable && getSortIcon(column.id)}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTableBody()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />
      
      {/* Dispute Details Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          {selectedDispute && (
            <>
              <SheetHeader className="px-0">
                <SheetTitle>Dispute Details - {selectedDispute.id}</SheetTitle>
                <SheetDescription>
                  View dispute information
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
                </TabsList>

                {/* Tab 1: Overview */}
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Customer</Label>
                      <Input value={selectedDispute.customer} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Dispute Type</Label>
                      <Input value={selectedDispute.type} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input value={`£${selectedDispute.amount.toLocaleString()}`} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Input value={selectedDispute.status} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Input value={selectedDispute.priority} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Assigned To</Label>
                      <Input value={selectedDispute.assignedTo} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Created Date</Label>
                      <Input value={format(new Date(selectedDispute.createdDate), "MMM dd, yyyy")} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input value={format(new Date(selectedDispute.dueDate), "MMM dd, yyyy")} readOnly />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={selectedDispute.description} readOnly rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Resolution Notes</Label>
                    <Textarea 
                      value={selectedDispute.resolutionNotes || "No resolution notes yet"}
                      readOnly
                      rows={4}
                    />
                  </div>
                </TabsContent>

                {/* Tab 2: Workflow */}
                <TabsContent value="workflow" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Active Workflow</CardTitle>
                      <CardDescription>{selectedDispute.workflow}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-sm font-medium">Current Level:</span>
                        <Badge>{selectedDispute.currentLevel} of {selectedDispute.totalLevels || getTotalLevelsForDisputeType(selectedDispute.type)}</Badge>
                      </div>

                      <div className="space-y-6">
                        {[
                          {
                            level: 1,
                            title: "Initial Review & Categorization",
                            description: "Dispute received and automatically categorized by AI",
                            completedDate: "2024-01-15",
                          },
                          {
                            level: 2,
                            title: "Investigation & Evidence Gathering",
                            description: "Collecting relevant documents and customer communications",
                            completedDate: selectedDispute.currentLevel >= 2 ? "2024-01-16" : null,
                          },
                          {
                            level: 3,
                            title: "Resolution Recommendation",
                            description: "AI generates resolution recommendation based on analysis",
                            completedDate: selectedDispute.currentLevel >= 3 ? "2024-01-17" : null,
                          },
                          {
                            level: 4,
                            title: "Final Approval & Closure",
                            description: "Management approval and customer notification",
                            completedDate: selectedDispute.currentLevel >= 4 ? "2024-01-18" : null,
                          },
                        ].map((step) => (
                          <div key={step.level} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                step.level <= selectedDispute.currentLevel
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground"
                              }`}>
                                {step.level < selectedDispute.currentLevel ? (
                                  <CheckCircle2 className="h-4 w-4" />
                                ) : step.level === selectedDispute.currentLevel ? (
                                  <Clock className="h-4 w-4" />
                                ) : (
                                  <span className="text-sm">{step.level}</span>
                                )}
                              </div>
                              {step.level < 4 && (
                                <div className={`w-0.5 h-12 ${
                                  step.level < selectedDispute.currentLevel
                                    ? "bg-primary"
                                    : "bg-muted"
                                }`} />
                              )}
                            </div>
                            <div className="flex-1 pb-8">
                              <h4 className="font-semibold">{step.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {step.description}
                              </p>
                              {step.completedDate && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Completed: {format(new Date(step.completedDate), "MMM dd, yyyy")}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 3: Activity */}
                <TabsContent value="activity" className="space-y-4">
                  <ConsolidatedActivityTimeline
                    activities={disputeActivityData?.activities ?? []}
                    communications={disputeActivityData?.communications ?? []}
                    notes={disputeActivityData?.notes ?? []}
                    isEditMode={false}
                  />
                </TabsContent>

                {/* Tab 4: Documents */}
                <TabsContent value="documents" className="space-y-4">
                  <Button className="w-full">
                    <FileText className="mr-2 h-4 w-4" />
                    Upload Document
                  </Button>
                  <div className="space-y-3">
                    {documents.map((doc, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.size} • Uploaded {format(new Date(doc.date), "MMM dd, yyyy")}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Tab 5: AI Insights */}
                <TabsContent value="ai-insights" className="space-y-4">
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        <CardTitle className="text-lg">AI-Powered Insights</CardTitle>
                      </div>
                      <CardDescription>
                        Automated analysis by Dispute Resolute Agent
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Confidence Score */}
                      {selectedDispute.aiScore !== null && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Confidence Score</Label>
                            <span className="text-sm font-medium">{selectedDispute.aiScore}%</span>
                          </div>
                          <Progress value={selectedDispute.aiScore} className="h-2" />
                        </div>
                      )}

                      {/* AI Recommendation */}
                      <div className="space-y-2">
                        <Label>AI Recommendation</Label>
                        <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                          <CardContent className="pt-4">
                            <p className="text-sm">{selectedDispute.aiRecommendation}</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Similar Cases */}
                      <div className="space-y-2">
                        <Label>Similar Cases Analysis</Label>
                        <p className="text-sm text-muted-foreground">
                          Based on analysis of 47 similar disputes, 82% were resolved with partial credit.
                          Average resolution time: 12 days.
                        </p>
                      </div>

                      {/* Risk Assessment */}
                      <div className="space-y-2">
                        <Label>Risk Assessment</Label>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Medium Risk
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            Customer has good payment history
                          </span>
                        </div>
                      </div>

                      {/* Suggested Actions */}
                      <div className="space-y-2">
                        <Label>Suggested Actions</Label>
                        <ul className="mt-2 space-y-2">
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Verify service delivery dates from system logs</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Review contract terms for billing cycle</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Consider 30% credit as resolution</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="mt-6 px-0 flex justify-end">
                <Button variant="outline" onClick={() => setDetailSheetOpen(false)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      
      {/* ── Floating Quick Actions Toolbar ─────────────────────────────────── */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedRows.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedRows.size === 1 ? "dispute" : "disputes"} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {renderQuickActions()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Call Modal */}
      <LogCallModal
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customerName={singleSelectedDispute?.customer || ""}
        customerPhone={singleSelectedDispute?.contactPhone}
        onSuccess={() => toast.success("Call logged to timeline")}
      />

      {/* Send Email Modal */}
      <SendEmailModal
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        customerName={singleSelectedDispute?.customer || ""}
        customerEmail={singleSelectedDispute?.contactEmail || ""}
        customerId={singleSelectedDispute?.customerId?.toString()}
        sourceType="dispute"
        onSuccess={() => toast.success("Email logged to timeline")}
      />

      {/* ── Add Note Modal ─────────────────────────────────────────────────── */}
      <AddNoteModal
        open={addNoteOpen}
        onOpenChange={setAddNoteOpen}
        disputeId={singleSelectedDispute?.id || ""}
        disputeLabel={singleSelectedDispute?.id || ""}
        customerName={singleSelectedDispute?.customer || ""}
        onSubmit={handleQuickAddNote}
        isPending={addDisputeNoteMutation.isPending}
      />

      {/* ── Reassign User Modal ─────────────────────────────────────────────── */}
      <Dialog open={reassignOpen} onOpenChange={(o) => { setReassignOpen(o); if (!o) setReassignUserId(""); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-primary" />
              Reassign User
            </DialogTitle>
            <DialogDescription>
              {selectedRows.size === 1
                ? `Reassign dispute ${singleSelectedDispute?.id} to a different user`
                : `Reassign ${selectedRows.size} disputes to a different user`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Assign To</Label>
            <Select value={reassignUserId} onValueChange={setReassignUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReassignOpen(false); setReassignUserId(""); }}>Cancel</Button>
            <Button onClick={handleQuickReassign} disabled={!reassignUserId} className="gap-2">
              <UserCog className="h-4 w-4" />
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Update Priority Modal ───────────────────────────────────────────── */}
      <UpdatePriorityModal
        open={updatePriorityOpen}
        onOpenChange={(o) => { setUpdatePriorityOpen(o); if (!o) setUpdatePriorityValue(""); }}
        selectedCount={selectedRows.size}
        selectedDisputeId={singleSelectedDispute?.id}
        priorities={disputePriorities}
        value={updatePriorityValue}
        onValueChange={setUpdatePriorityValue}
        onConfirm={handleQuickUpdatePriority}
      />

      {/* ── Resolve Dispute Modal ───────────────────────────────────────────── */}
      <ResolveDisputeModal
        open={resolveDisputeOpen}
        onOpenChange={setResolveDisputeOpen}
        disputeId={singleSelectedDispute?.id}
        customerName={singleSelectedDispute?.customer}
        onResolve={handleQuickResolveDispute}
        idPrefix="tab-"
      />

      {/* ── Close Dispute Modal ─────────────────────────────────────────────── */}
      <Dialog open={closeDisputeOpen} onOpenChange={(o) => { setCloseDisputeOpen(o); if (!o) { setCloseReason('duplicate'); setCloseNote(''); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Close Dispute</DialogTitle>
            <DialogDescription>
              Close this dispute without completing all workflow steps. This action will mark the dispute as closed and remove it from active views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tab-close-reason">Reason for Closing</Label>
              <Select value={closeReason} onValueChange={(value) => setCloseReason(value as 'duplicate' | 'customer_withdrew' | 'invalid_dispute' | 'other')}>
                <SelectTrigger id="tab-close-reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate Entry</SelectItem>
                  <SelectItem value="customer_withdrew">Customer Withdrew Dispute</SelectItem>
                  <SelectItem value="invalid_dispute">Invalid Dispute</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tab-close-note">
                Additional Notes {closeReason === 'other' && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                id="tab-close-note"
                placeholder="Provide additional context for closing this dispute..."
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                rows={4}
              />
              {closeReason === 'other' && !closeNote && (
                <p className="text-sm text-muted-foreground">Note is required when selecting "Other" as the reason.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCloseDisputeOpen(false); setCloseReason('duplicate'); setCloseNote(''); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleQuickCloseDispute} disabled={closeReason === 'other' && !closeNote}>
              <XCircle className="mr-2 h-4 w-4" />
              Close Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Dispute Confirmation Dialog */}
      {renderReopenDialog()}

    </div>
  );
}
